import { useCallback } from 'react';
import { useLocation } from 'react-router';
import { useAuth } from '@modules/root/context';
import { useFeature } from '~/modules/root';
import { useOpenSaveGate } from '~/modules-rocco/design-board/save-gate-state';
import type { PinItemType } from '~/modules/moodboards/type';

interface GuestPinContext {
    pinId: number;
    pinType: PinItemType;
    libraryItemId?: number;
    items?: { id: number; libraryItemId?: number }[];
    /** Item name, shown on the gate's success screen. */
    title?: string;
    /**
     * Item image for the gate's brand pane and success thumbnail. When absent
     * the gate falls back to the page's gate subject.
     */
    image?: string;
}

/**
 * Hook that manages the guest save flow for the FollowSelector.
 *
 * Behind `revamp_2026` a logged-out save opens the save gate, which keeps
 * the pin through auth and resumes it into a board the reader picks. On the
 * legacy path we only open the auth modal; users choose a board explicitly
 * after login by clicking save again.
 */
export const useGuestSave = () => {
    const { showAuthModal } = useAuth();
    const saveGateEnabled = useFeature('revamp_2026');
    const openSaveGate = useOpenSaveGate();
    const { pathname, search } = useLocation();

    /**
     * Route a logged-out save attempt into the right flow.
     * Called when a logged-out user clicks a heart/save icon.
     */
    const guestSave = useCallback(
        (pin: GuestPinContext) => {
            if (saveGateEnabled) {
                openSaveGate({
                    action: 'save',
                    pinId: pin.pinId,
                    pinType: pin.pinType,
                    libraryItemId: pin.libraryItemId,
                    items: pin.items,
                    title: pin.title,
                    image: pin.image,
                });
                return;
            }
            const currentUrl = `${pathname}${search}`;
            showAuthModal({
                authSource: 'designBoard',
                redirect: currentUrl,
                skipPostSignUp: true,
            });
        },
        [saveGateEnabled, openSaveGate, pathname, search, showAuthModal],
    );

    return { guestSave };
};
